import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, type Control } from 'react-hook-form';
import { z } from 'zod';
import { Plus, X } from 'lucide-react';
import {
  apiClient,
  type AdminCoupon,
  type AdminCouponDuration,
  type AdminCreateCouponInput,
  type AdminUpdateCouponInput,
  type ApiError,
} from '@/lib/api-client';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  const apiErr = err as ApiError | undefined;
  if (apiErr?.message) return apiErr.message;
  return 'Erro inesperado. Tente novamente.';
}

const amountFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const DURATION_LABEL: Record<AdminCouponDuration, string> = {
  once: 'Uma vez',
  forever: 'Para sempre',
  repeating: 'Recorrente',
};

function formatDiscount(coupon: AdminCoupon): string {
  if (coupon.discountType === 'percent') {
    return `${coupon.percentOff ?? 0}%`;
  }
  return amountFormatter.format((coupon.amountOffCents ?? 0) / 100);
}

function formatDuration(coupon: AdminCoupon): string {
  const label = DURATION_LABEL[coupon.duration] ?? coupon.duration;
  if (coupon.duration === 'repeating' && coupon.durationInMonths) {
    return `${label} por ${coupon.durationInMonths}m`;
  }
  return label;
}

function formatValidity(coupon: AdminCoupon): string {
  const parts: string[] = [];
  if (coupon.maxRedemptions) parts.push(`${coupon.maxRedemptions} usos`);
  if (coupon.expiresAt) {
    parts.push(`até ${new Date(coupon.expiresAt).toLocaleDateString('pt-BR')}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Sem limite';
}

// ---------------------------------------------------------------------------
// Painel de criação/edição
// ---------------------------------------------------------------------------

const couponFormSchema = z
  .object({
    code: z
      .string()
      .trim()
      .min(3, 'Código precisa ter pelo menos 3 caracteres')
      .max(40, 'Código muito longo (máx. 40)'),
    name: z.string().trim().min(1, 'Nome obrigatório').max(120, 'Nome muito longo'),
    description: z.string().trim().max(2000, 'Descrição muito longa').optional(),
    discountType: z.enum(['percent', 'amount']),
    percentOff: z
      .number({ invalid_type_error: 'Percentual inválido' })
      .int('Percentual deve ser inteiro')
      .min(1, 'Mínimo 1%')
      .max(100, 'Máximo 100%')
      .optional(),
    amountOffReais: z
      .number({ invalid_type_error: 'Valor inválido' })
      .min(0.01, 'Valor deve ser maior que zero')
      .optional(),
    currency: z.string().trim().toLowerCase().length(3, 'Moeda deve ter 3 letras'),
    duration: z.enum(['once', 'forever', 'repeating']),
    durationInMonths: z
      .number({ invalid_type_error: 'Duração inválida' })
      .int('Duração deve ser inteira')
      .min(1, 'Mínimo 1 mês')
      .optional(),
    maxRedemptions: z
      .number({ invalid_type_error: 'Limite inválido' })
      .int('Limite deve ser inteiro')
      .min(1, 'Mínimo 1 uso')
      .optional(),
    // yyyy-mm-dd do <input type="date"> — convertido para ISO no submit.
    expiresAt: z.string().optional(),
    active: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.discountType === 'percent' && data.percentOff === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['percentOff'],
        message: "Percentual obrigatório quando o desconto é 'Porcentagem'",
      });
    }
    if (data.discountType === 'amount' && data.amountOffReais === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['amountOffReais'],
        message: "Valor obrigatório quando o desconto é 'Valor fixo'",
      });
    }
    if (data.duration === 'repeating' && data.durationInMonths === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['durationInMonths'],
        message: "Duração em meses obrigatória quando a recorrência é 'Recorrente'",
      });
    }
  });

type CouponFormValues = z.infer<typeof couponFormSchema>;

/** Converte number|null do backend para number|undefined do form (input vazio). */
function nullToUndefined(value: number | null): number | undefined {
  return value === null ? undefined : value;
}

function couponToFormValues(coupon: AdminCoupon): CouponFormValues {
  return {
    code: coupon.code,
    name: coupon.name,
    description: coupon.description ?? '',
    discountType: coupon.discountType,
    percentOff: nullToUndefined(coupon.percentOff),
    amountOffReais:
      coupon.amountOffCents !== null ? coupon.amountOffCents / 100 : undefined,
    currency: coupon.currency,
    duration: coupon.duration,
    durationInMonths: nullToUndefined(coupon.durationInMonths),
    maxRedemptions: nullToUndefined(coupon.maxRedemptions),
    expiresAt: coupon.expiresAt ? coupon.expiresAt.slice(0, 10) : '',
    active: coupon.active,
  };
}

const EMPTY_FORM_VALUES: CouponFormValues = {
  code: '',
  name: '',
  description: '',
  discountType: 'percent',
  percentOff: undefined,
  amountOffReais: undefined,
  currency: 'brl',
  duration: 'once',
  durationInMonths: undefined,
  maxRedemptions: undefined,
  expiresAt: '',
  active: true,
};

/** Coage input number vazio para `undefined` em vez de NaN (mesmo padrão de campos opcionais). */
function toOptionalNumber(value: string): number | undefined {
  return value === '' ? undefined : Number(value);
}

/** Toggle reutilizavel (mesmo padrao de admin/planos.tsx). */
function ToggleField({
  control,
  name,
  label,
  hint,
}: {
  control: Control<CouponFormValues>;
  name: 'active';
  label: (checked: boolean) => string;
  hint?: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className="flex items-start gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={field.value}
            onClick={() => field.onChange(!field.value)}
            className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 focus:ring-offset-neutral-900 ${
              field.value ? 'bg-violet-600' : 'bg-neutral-700'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                field.value ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
          <div>
            <span className="text-sm text-neutral-300">{label(field.value)}</span>
            {hint && <p className="text-xs text-neutral-500">{hint}</p>}
          </div>
        </div>
      )}
    />
  );
}

interface CouponFormPanelProps {
  /** null = criando um cupom novo. */
  coupon: AdminCoupon | null;
  onClose: () => void;
  onSaved: () => void;
}

function CouponFormPanel({ coupon, onClose, onSaved }: CouponFormPanelProps) {
  const isEditing = coupon !== null;

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<CouponFormValues>({
    resolver: zodResolver(couponFormSchema),
    defaultValues: coupon ? couponToFormValues(coupon) : EMPTY_FORM_VALUES,
  });

  const discountType = watch('discountType');
  const duration = watch('duration');

  const saveMutation = useMutation({
    mutationFn: (values: CouponFormValues) => {
      const description = values.description?.trim() ? values.description.trim() : null;
      const expiresAt = values.expiresAt?.trim()
        ? new Date(`${values.expiresAt}T23:59:59`).toISOString()
        : undefined;
      const percentOff = values.discountType === 'percent' ? values.percentOff : undefined;
      const amountOffCents =
        values.discountType === 'amount' && values.amountOffReais !== undefined
          ? Math.round(values.amountOffReais * 100)
          : undefined;
      const durationInMonths =
        values.duration === 'repeating' ? values.durationInMonths : undefined;

      if (coupon) {
        const input: AdminUpdateCouponInput = {
          name: values.name,
          description,
          discountType: values.discountType,
          percentOff,
          amountOffCents,
          currency: values.currency,
          duration: values.duration,
          durationInMonths,
          maxRedemptions: values.maxRedemptions,
          expiresAt,
          active: values.active,
        };
        return apiClient.adminUpdateCoupon(coupon.id, input);
      }

      const input: AdminCreateCouponInput = {
        code: values.code,
        name: values.name,
        description,
        discountType: values.discountType,
        percentOff,
        amountOffCents,
        currency: values.currency,
        duration: values.duration,
        durationInMonths,
        maxRedemptions: values.maxRedemptions,
        expiresAt,
        active: values.active,
      };
      return apiClient.adminCreateCoupon(input);
    },
    onSuccess: () => onSaved(),
  });

  function onSubmit(values: CouponFormValues) {
    saveMutation.mutate(values);
  }

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-neutral-100">
            {isEditing ? `Editar cupom — ${coupon.name}` : 'Novo cupom'}
          </h2>
          <p className="mt-0.5 text-sm text-neutral-400">
            {isEditing
              ? 'O código do cupom não pode ser alterado depois de criado.'
              : 'O código não pode ser alterado depois de criado — escolha com cuidado.'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="coupon-code" className="block text-sm font-medium text-neutral-300">
              Código
            </label>
            <input
              id="coupon-code"
              type="text"
              {...register('code')}
              disabled={isEditing}
              placeholder="ex: BEMVINDO10"
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500 disabled:cursor-not-allowed disabled:opacity-50"
            />
            {isEditing ? (
              <p className="text-xs text-neutral-500">
                Identificador estável usado no checkout. Somente leitura.
              </p>
            ) : (
              <p className="text-xs text-neutral-500">
                É normalizado para maiúsculas ao salvar.
              </p>
            )}
            {errors.code && <p className="text-xs text-red-400">{errors.code.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="coupon-name" className="block text-sm font-medium text-neutral-300">
              Nome
            </label>
            <input
              id="coupon-name"
              type="text"
              {...register('name')}
              placeholder="ex: Boas-vindas 10%"
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            />
            {errors.name && <p className="text-xs text-red-400">{errors.name.message}</p>}
          </div>
        </div>

        <div className="space-y-1.5">
          <label
            htmlFor="coupon-description"
            className="block text-sm font-medium text-neutral-300"
          >
            Descrição
          </label>
          <textarea
            id="coupon-description"
            {...register('description')}
            rows={2}
            placeholder="Descrição interna do cupom (opcional)"
            className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
          />
          {errors.description && (
            <p className="text-xs text-red-400">{errors.description.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label
              htmlFor="coupon-discount-type"
              className="block text-sm font-medium text-neutral-300"
            >
              Tipo de desconto
            </label>
            <select
              id="coupon-discount-type"
              {...register('discountType')}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            >
              <option value="percent">Porcentagem</option>
              <option value="amount">Valor fixo</option>
            </select>
          </div>

          {discountType === 'percent' ? (
            <div className="space-y-1.5">
              <label
                htmlFor="coupon-percent-off"
                className="block text-sm font-medium text-neutral-300"
              >
                Percentual (%)
              </label>
              <input
                id="coupon-percent-off"
                type="number"
                step="1"
                min="1"
                max="100"
                {...register('percentOff', { setValueAs: toOptionalNumber })}
                className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
              />
              {errors.percentOff && (
                <p className="text-xs text-red-400">{errors.percentOff.message}</p>
              )}
            </div>
          ) : (
            <div className="space-y-1.5">
              <label
                htmlFor="coupon-amount-off"
                className="block text-sm font-medium text-neutral-300"
              >
                Valor (R$)
              </label>
              <input
                id="coupon-amount-off"
                type="number"
                step="0.01"
                min="0.01"
                {...register('amountOffReais', { setValueAs: toOptionalNumber })}
                className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
              />
              {errors.amountOffReais && (
                <p className="text-xs text-red-400">{errors.amountOffReais.message}</p>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <label
              htmlFor="coupon-currency"
              className="block text-sm font-medium text-neutral-300"
            >
              Moeda
            </label>
            <select
              id="coupon-currency"
              {...register('currency')}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            >
              <option value="brl">BRL</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="coupon-duration"
              className="block text-sm font-medium text-neutral-300"
            >
              Recorrência
            </label>
            <select
              id="coupon-duration"
              {...register('duration')}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            >
              <option value="once">Uma vez</option>
              <option value="forever">Para sempre</option>
              <option value="repeating">Recorrente (N meses)</option>
            </select>
          </div>

          {duration === 'repeating' && (
            <div className="space-y-1.5">
              <label
                htmlFor="coupon-duration-months"
                className="block text-sm font-medium text-neutral-300"
              >
                Duração (meses)
              </label>
              <input
                id="coupon-duration-months"
                type="number"
                step="1"
                min="1"
                {...register('durationInMonths', { setValueAs: toOptionalNumber })}
                className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
              />
              {errors.durationInMonths && (
                <p className="text-xs text-red-400">{errors.durationInMonths.message}</p>
              )}
            </div>
          )}
        </div>

        {isEditing && (
          <p className="rounded-md border border-amber-900/50 bg-amber-950/30 px-3 py-2 text-xs text-amber-200">
            Tipo de desconto, valor e recorrência não são re-sincronizados com o Stripe depois de
            criados. Para mudar o desconto de verdade, crie um cupom novo.
          </p>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label
              htmlFor="coupon-max-redemptions"
              className="block text-sm font-medium text-neutral-300"
            >
              Limite de usos
            </label>
            <input
              id="coupon-max-redemptions"
              type="number"
              step="1"
              min="1"
              placeholder="Sem limite"
              {...register('maxRedemptions', { setValueAs: toOptionalNumber })}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            />
            {errors.maxRedemptions && (
              <p className="text-xs text-red-400">{errors.maxRedemptions.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="coupon-expires-at"
              className="block text-sm font-medium text-neutral-300"
            >
              Validade
            </label>
            <input
              id="coupon-expires-at"
              type="date"
              {...register('expiresAt')}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            />
            {errors.expiresAt && (
              <p className="text-xs text-red-400">{errors.expiresAt.message}</p>
            )}
          </div>
        </div>

        <div className="flex items-end pb-2">
          <ToggleField
            control={control}
            name="active"
            label={(checked) => `Cupom ${checked ? 'ativo' : 'inativo'}`}
            hint="Cupons inativos não podem ser aplicados no checkout."
          />
        </div>

        {saveMutation.isError && (
          <p className="text-sm text-red-400">{getErrorMessage(saveMutation.error)}</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-700 px-4 py-2 text-sm font-medium text-neutral-300 hover:bg-neutral-800"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-900 hover:bg-white disabled:opacity-50"
          >
            {saveMutation.isPending
              ? 'Salvando...'
              : isEditing
                ? 'Salvar alterações'
                : 'Criar cupom'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
      <div className="divide-y divide-neutral-800">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3">
            <div className="h-4 w-32 animate-pulse rounded bg-neutral-800" />
            <div className="h-4 w-20 animate-pulse rounded bg-neutral-800" />
            <div className="h-4 w-16 animate-pulse rounded bg-neutral-800" />
            <div className="ml-auto h-4 w-24 animate-pulse rounded bg-neutral-800" />
          </div>
        ))}
      </div>
    </div>
  );
}

type PanelState = 'closed' | 'new' | AdminCoupon;

export default function AdminCuponsPage() {
  const queryClient = useQueryClient();
  const [panel, setPanel] = useState<PanelState>('closed');

  const {
    data: coupons,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ['admin', 'coupons'],
    queryFn: () => apiClient.adminListCoupons(),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'coupons'] });
  }

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => apiClient.adminDeleteCoupon(id),
    onSuccess: invalidate,
    onError: (err) => window.alert(getErrorMessage(err)),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => apiClient.adminUpdateCoupon(id, { active: true }),
    onSuccess: invalidate,
    onError: (err) => window.alert(getErrorMessage(err)),
  });

  const isBusy = deactivateMutation.isPending || reactivateMutation.isPending;

  function handleToggleActive(coupon: AdminCoupon) {
    if (coupon.active) {
      if (
        !window.confirm(`Desativar o cupom "${coupon.code}"? Ele deixa de valer no checkout.`)
      )
        return;
      deactivateMutation.mutate(coupon.id);
    } else {
      reactivateMutation.mutate(coupon.id);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-100">Cupons</h1>
          <p className="mt-1 text-sm text-neutral-400">
            Cupons de desconto do checkout, sincronizados com o Stripe.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPanel('new')}
          className="inline-flex items-center gap-1.5 rounded-md bg-neutral-100 px-3 py-1.5 text-sm font-medium text-neutral-900 hover:bg-neutral-200"
        >
          <Plus className="h-4 w-4" />
          Novo cupom
        </button>
      </header>

      {isPending ? (
        <LoadingSkeleton />
      ) : isError ? (
        <div className="rounded-lg border border-red-900/50 bg-red-950/30 p-4 text-sm text-red-200">
          Não foi possível carregar os cupons agora.
          {error instanceof Error && error.message ? (
            <span className="ml-1 opacity-70">({error.message})</span>
          ) : null}
        </div>
      ) : !coupons || coupons.length === 0 ? (
        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-8 text-center">
          <p className="text-sm text-neutral-400">Nenhum cupom cadastrado ainda.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
          <table className="w-full text-sm">
            <thead className="border-b border-neutral-800 bg-neutral-950/40 text-left text-xs font-medium uppercase tracking-wide text-neutral-400">
              <tr>
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">Desconto</th>
                <th className="px-4 py-3">Duração</th>
                <th className="px-4 py-3">Validade</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {coupons.map((coupon) => (
                <tr key={coupon.id} className="hover:bg-neutral-800/40">
                  <td className="px-4 py-3">
                    <div className="font-mono text-sm font-medium text-neutral-100">
                      {coupon.code}
                    </div>
                    <div className="text-xs text-neutral-500">{coupon.name}</div>
                  </td>
                  <td className="px-4 py-3 text-neutral-300">{formatDiscount(coupon)}</td>
                  <td className="px-4 py-3 text-neutral-300">{formatDuration(coupon)}</td>
                  <td className="px-4 py-3 text-neutral-300">{formatValidity(coupon)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        coupon.active
                          ? 'bg-emerald-900/40 text-emerald-200'
                          : 'bg-neutral-800 text-neutral-400'
                      }`}
                    >
                      {coupon.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setPanel(coupon)}
                        className="rounded-md border border-neutral-700 px-2.5 py-1 text-xs font-medium text-neutral-300 hover:bg-neutral-800"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleActive(coupon)}
                        disabled={isBusy}
                        className="rounded-md border border-neutral-700 px-2.5 py-1 text-xs font-medium text-neutral-300 hover:bg-neutral-800 disabled:opacity-50"
                      >
                        {coupon.active ? 'Desativar' : 'Ativar'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {panel !== 'closed' && (
        <CouponFormPanel
          key={panel === 'new' ? 'new' : panel.id}
          coupon={panel === 'new' ? null : panel}
          onClose={() => setPanel('closed')}
          onSaved={() => {
            setPanel('closed');
            invalidate();
          }}
        />
      )}
    </div>
  );
}
